(() => {
  'use strict';

  /*
   * =========================================================
   * CONFIG
   * =========================================================
   */

  const CONFIG = {
    sizeColumn: 1,

    classes: {
      hideSingleSize:
        'sj-bundlex-hide-single-size',

      hideVariants:
        'sj-bundlex-hide-offer-variants',

      hideStockOffer:
        'sj-bundlex-hide-stock-offer',

      hideStockSwatch:
        'sj-bundlex-hide-stock-swatch',

      hideAllOffers:
        'sj-bundlex-hide-all-offers'
    },

    maxInitAttempts: 30,
    retryDelay: 250
  };


  /*
   * =========================================================
   * STATE
   * =========================================================
   */

  let bundlexRoot = null;
  let observer = null;
  let updateScheduled = false;
  let initAttempts = 0;
  let autoSelecting = false;


  /*
   * =========================================================
   * HELPERS
   * =========================================================
   */

  function normalise(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }


  function getOfferName(offer) {
    const title =
      offer.querySelector(
        '.bx-offer-title'
      );

    return title
      ? normalise(title.textContent)
      : '';
  }


  function isBuy1(name) {
    return /^buy\s*1$/i.test(
      normalise(name)
    );
  }


  function isBuy3(name) {
    return /^buy\s*3\b/i.test(
      normalise(name)
    );
  }


  function isBuy6(name) {
    return /^buy\s*6\b/i.test(
      normalise(name)
    );
  }


  function isCarton(name) {
    return /\bcarton\b/i.test(
      normalise(name)
    );
  }


  function shouldHideVariantSelector(name) {
    return (
      isBuy1(name) ||
      isCarton(name)
    );
  }


  /*
   * =========================================================
   * SHOPIFY VARIANT / INVENTORY
   * =========================================================
   */

  function getShopifyVariants() {
    return (
      window.SJ_BUNDLEX_PRODUCT &&
      Array.isArray(
        window.SJ_BUNDLEX_PRODUCT.variants
      )
    )
      ? window.SJ_BUNDLEX_PRODUCT.variants
      : [];
  }


  /*
   * Get currently selected Shopify variant.
   *
   * URL first because this theme normally
   * updates ?variant= when variant changes.
   */
  function getSelectedVariantId() {

    /*
     * 1. URL FIRST
     */
    const params =
      new URLSearchParams(
        window.location.search
      );

    const urlVariant =
      params.get('variant');

    if (
      urlVariant &&
      getShopifyVariants().some(
        (variant) =>
          String(variant.id) ===
          String(urlVariant)
      )
    ) {
      return String(
        urlVariant
      );
    }


    /*
     * 2. MAIN PRODUCT FORM
     */
    const mainProductInput =
      document.querySelector(
        [
          '.hdt-product-info form[action*="/cart/add"] [name="id"]',
          '.hdt-product-info__item form[action*="/cart/add"] [name="id"]',
          'product-form form[action*="/cart/add"] [name="id"]'
        ].join(',')
      );


    if (
      mainProductInput &&
      mainProductInput.value &&
      getShopifyVariants().some(
        (variant) =>
          String(variant.id) ===
          String(mainProductInput.value)
      )
    ) {
      return String(
        mainProductInput.value
      );
    }


    /*
     * 3. GENERIC PRODUCT FORM FALLBACK
     */
    const inputs =
      Array.from(
        document.querySelectorAll(
          'form[action*="/cart/add"] [name="id"]'
        )
      );


    const validInput =
      inputs.find(
        (input) =>
          input.value &&
          getShopifyVariants().some(
            (variant) =>
              String(variant.id) ===
              String(input.value)
          )
      );


    if (validInput) {
      return String(
        validInput.value
      );
    }


    return null;
  }


  function getVariantStock(variant) {

    if (!variant) {
      return 0;
    }


    /*
     * Continue selling when out of stock.
     */
    if (
      variant.inventory_policy ===
      'continue'
    ) {
      return Infinity;
    }


    /*
     * Inventory not tracked.
     */
    if (
      !variant.inventory_management
    ) {
      return Infinity;
    }


    const qty =
      Number(
        variant.inventory_quantity
      );


    return Number.isFinite(qty)
      ? Math.max(0, qty)
      : 0;
  }


  function getSelectedVariant() {

    const selectedId =
      getSelectedVariantId();


    if (!selectedId) {
      return null;
    }


    return (
      getShopifyVariants().find(
        (variant) =>
          String(variant.id) ===
          String(selectedId)
      ) || null
    );
  }


  function getTotalAvailableInventory() {

    const variants =
      getShopifyVariants();

    let total = 0;


    for (
      const variant of variants
    ) {

      const qty =
        getVariantStock(
          variant
        );


      if (qty === Infinity) {
        return Infinity;
      }


      total += qty;
    }


    return total;
  }


  /*
   * =========================================================
   * OFFER QUANTITY
   * =========================================================
   */

  function getOfferQuantity(offer) {

    const name =
      getOfferName(
        offer
      );


    if (isCarton(name)) {

      const groups =
        offer.querySelectorAll(
          '.js-bx-variant-group'
        );


      if (groups.length) {
        return groups.length;
      }


      const numbered =
        Array.from(
          offer.querySelectorAll(
            '[data-nr]'
          )
        )
          .map(
            (element) =>
              Number(
                element.getAttribute(
                  'data-nr'
                )
              )
          )
          .filter(
            Number.isFinite
          );


      if (numbered.length) {
        return Math.max(
          ...numbered
        );
      }


      return null;
    }


    if (isBuy6(name)) {
      return 6;
    }


    if (isBuy3(name)) {
      return 3;
    }


    if (isBuy1(name)) {
      return 1;
    }


    const match =
      name.match(
        /^buy\s*(\d+)/i
      );


    if (match) {
      return Number(
        match[1]
      );
    }


    return null;
  }


  /*
   * =========================================================
   * FEATURE 1
   *
   * HIDE SIZE IF ONLY ONE SIZE
   * =========================================================
   */

  function getOptionColumn(group) {

    const swatches =
      Array.from(
        group.querySelectorAll(
          '.bx-swatch[data-col]'
        )
      );


    if (!swatches.length) {
      return null;
    }


    const columns =
      new Set(
        swatches.map(
          (swatch) =>
            Number(
              swatch.getAttribute(
                'data-col'
              )
            )
        )
      );


    if (
      columns.size !== 1
    ) {
      return null;
    }


    return [...columns][0];
  }


  function getUniqueOptionCount(group) {

    const values =
      Array.from(
        group.querySelectorAll(
          '.bx-swatch'
        )
      )
        .filter(
          (swatch) =>
            !swatch.hidden &&
            swatch.getAttribute(
              'aria-hidden'
            ) !== 'true'
        )
        .map(
          (swatch) =>
            normalise(
              swatch.dataset.value ||
              swatch.getAttribute(
                'data-value'
              ) ||
              swatch.getAttribute(
                'value'
              ) ||
              swatch.getAttribute(
                'title'
              ) ||
              swatch.textContent
            )
        )
        .filter(Boolean);


    return new Set(
      values
    ).size;
  }


  function updateSingleSizeOptions() {

    if (!bundlexRoot) {
      return;
    }


    const groups =
      bundlexRoot.querySelectorAll(
        '.bx-swatch-group'
      );


    groups.forEach(
      (group) => {

        if (
          getOptionColumn(group) !==
          CONFIG.sizeColumn
        ) {
          return;
        }


        const count =
          getUniqueOptionCount(
            group
          );


        if (!count) {
          return;
        }


        group.classList.toggle(
          CONFIG.classes
            .hideSingleSize,

          count === 1
        );
      }
    );
  }


  /*
   * =========================================================
   * FEATURE 2
   *
   * HIDE INTERNAL VARIANT PICKER
   * FOR BUY 1 / CARTON
   * =========================================================
   */

  function updateVariantSelectorVisibility() {

    if (!bundlexRoot) {
      return;
    }


    const offers =
      bundlexRoot.querySelectorAll(
        '.js-bx-offer'
      );


    offers.forEach(
      (offer) => {

        const name =
          getOfferName(
            offer
          );


        const variantWrap =
          offer.querySelector(
            '.bx-offer-variants-wrap'
          );


        if (!variantWrap) {
          return;
        }


        variantWrap.classList.toggle(
          CONFIG.classes
            .hideVariants,

          shouldHideVariantSelector(
            name
          )
        );
      }
    );
  }


  /*
   * =========================================================
   * FEATURE 3
   *
   * CHECK IF OFFER HAS ENOUGH STOCK
   * =========================================================
   */

  function hasEnoughStock(offer) {

    const name =
      getOfferName(
        offer
      );


    const requiredQty =
      getOfferQuantity(
        offer
      );


    if (
      !requiredQty ||
      requiredQty < 1
    ) {
      return true;
    }


    /*
     * CARTON
     */
    if (isCarton(name)) {

      const selectedVariant =
        getSelectedVariant();


      if (!selectedVariant) {
        return true;
      }


      const available =
        getVariantStock(
          selectedVariant
        );


      return (
        available === Infinity ||
        available >= requiredQty
      );
    }


    /*
     * BUY 1
     */
    if (isBuy1(name)) {

      const selectedVariant =
        getSelectedVariant();


      if (!selectedVariant) {
        return true;
      }


      const available =
        getVariantStock(
          selectedVariant
        );


      return (
        available === Infinity ||
        available >= 1
      );
    }


    /*
     * BUY 3 / BUY 6
     */
    const totalInventory =
      getTotalAvailableInventory();


    return (
      totalInventory === Infinity ||
      totalInventory >= requiredQty
    );
  }


  /*
   * =========================================================
   * FEATURE 4
   *
   * HIDE WHOLE OFFER WHEN NOT ENOUGH STOCK
   * =========================================================
   */

  function updateStockVisibility() {

    if (!bundlexRoot) {
      return;
    }


    const offers =
      bundlexRoot.querySelectorAll(
        '.js-bx-offer'
      );


    offers.forEach(
      (offer) => {

        const enough =
          hasEnoughStock(
            offer
          );


        offer.classList.toggle(
          CONFIG.classes
            .hideStockOffer,

          !enough
        );
      }
    );


    ensureVisibleOfferSelected();
  }


  /*
   * =========================================================
   * FEATURE 5
   *
   * HIDE COMPLETE BUNDLEX WIDGET IF
   * SHOPIFY VARIANT IS SOLD OUT
   * =========================================================
   */

  function updateBundlexVisibilityForSelectedVariant() {

    if (!bundlexRoot) {
      return false;
    }


    const selectedVariant =
      getSelectedVariant();


    /*
     * Variant not yet resolved.
     */
    if (!selectedVariant) {
      return false;
    }


    /*
     * Use Shopify's own availability flag.
     */
    const unavailableByShopify =
      selectedVariant.available === false;


    const stock =
      getVariantStock(
        selectedVariant
      );


    const unavailableByInventory =
      (
        stock !== Infinity &&
        stock <= 0
      );


    const isOutOfStock =
      (
        unavailableByShopify ||
        unavailableByInventory
      );


    /*
     * Find COMPLETE Bundlex widget.
     */
    let widgetContainer = null;


    if (
      bundlexRoot.matches &&
      bundlexRoot.matches(
        '.bx-widget-container'
      )
    ) {

      widgetContainer =
        bundlexRoot;

    }


    if (!widgetContainer) {

      widgetContainer =
        bundlexRoot.closest(
          '.bx-widget-container'
        );

    }


    if (!widgetContainer) {

      widgetContainer =
        bundlexRoot.querySelector(
          '.bx-widget-container'
        );

    }


    if (!widgetContainer) {

      widgetContainer =
        bundlexRoot.closest(
          '.bx-widget'
        ) ||
        bundlexRoot.querySelector(
          '.bx-widget'
        );

    }


    const target =
      widgetContainer ||
      bundlexRoot;


    target.classList.toggle(
      CONFIG.classes
        .hideAllOffers,

      isOutOfStock
    );


    return isOutOfStock;
  }


  /*
   * =========================================================
   * FEATURE 6
   *
   * SELECT FIRST AVAILABLE OFFER IF ACTIVE
   * OFFER BECOMES HIDDEN
   * =========================================================
   */

  function ensureVisibleOfferSelected() {

    if (!bundlexRoot) {
      return;
    }


    const selected =
      bundlexRoot.querySelector(
        [
          '.js-bx-offer.bx-active',
          '.js-bx-offer[aria-checked="true"]'
        ].join(',')
      );


    if (!selected) {
      return;
    }


    if (
      !selected.classList.contains(
        CONFIG.classes
          .hideStockOffer
      )
    ) {
      return;
    }


    const offers =
      Array.from(
        bundlexRoot.querySelectorAll(
          '.js-bx-offer'
        )
      );


    const availableOffer =
      offers.find(
        (offer) =>
          !offer.classList.contains(
            CONFIG.classes
              .hideStockOffer
          ) &&
          getComputedStyle(
            offer
          ).display !== 'none'
      );


    if (
      availableOffer &&
      availableOffer !== selected
    ) {
      availableOffer.click();
    }
  }


  /*
   * =========================================================
   * FEATURE 7
   *
   * READ SELECTED BUNDLEX OPTIONS
   * =========================================================
   */

  function getSelectedBundlexOptions(
    variantGroup
  ) {

    const selected = [];


    const optionGroups =
      variantGroup.querySelectorAll(
        '.bx-swatch-group'
      );


    optionGroups.forEach(
      (group) => {

        const active =
          group.querySelector(
            '.bx-swatch--active'
          );


        if (!active) {
          return;
        }


        const column =
          Number(
            active.getAttribute(
              'data-col'
            )
          );


        if (
          !Number.isFinite(column)
        ) {
          return;
        }


        const value =
          normalise(
            active.getAttribute(
              'data-value'
            ) ||
            active.dataset.value ||
            active.getAttribute(
              'title'
            ) ||
            active.textContent
          );


        if (!value) {
          return;
        }


        selected[column] =
          value;
      }
    );


    return selected;
  }


  /*
   * =========================================================
   * MATCH BUNDLEX OPTIONS TO SHOPIFY VARIANT
   * =========================================================
   */

  function findShopifyVariantByOptions(
    optionValues
  ) {

    if (
      !Array.isArray(optionValues) ||
      !optionValues.length
    ) {
      return null;
    }


    const variants =
      getShopifyVariants();


    return (
      variants.find(
        (variant) => {

          if (
            !Array.isArray(
              variant.options
            )
          ) {
            return false;
          }


          if (
            variant.options.length !==
            optionValues.length
          ) {
            return false;
          }


          return variant.options.every(
            (value, index) => {

              return (
                normalise(value) ===
                normalise(
                  optionValues[index]
                )
              );

            }
          );
        }
      ) || null
    );
  }


  /*
   * =========================================================
   * SIMULATE VARIANT FROM SWATCH
   * =========================================================
   */

  function getVariantForSwatch(
    variantGroup,
    swatch
  ) {

    const options =
      getSelectedBundlexOptions(
        variantGroup
      );


    const column =
      Number(
        swatch.getAttribute(
          'data-col'
        )
      );


    if (
      !Number.isFinite(column)
    ) {
      return null;
    }


    const value =
      normalise(
        swatch.getAttribute(
          'data-value'
        ) ||
        swatch.dataset.value ||
        swatch.getAttribute(
          'title'
        ) ||
        swatch.textContent
      );


    if (!value) {
      return null;
    }


    options[column] =
      value;


    for (
      let index = 0;
      index < options.length;
      index++
    ) {

      if (!options[index]) {
        return null;
      }

    }


    return findShopifyVariantByOptions(
      options
    );
  }


  /*
   * =========================================================
   * FEATURE 8
   *
   * SEQUENTIAL STOCK ALLOCATION
   * =========================================================
   */

  function updateBundlexVariantInventory() {

    if (!bundlexRoot) {
      return;
    }


    const offers =
      bundlexRoot.querySelectorAll(
        '.js-bx-offer'
      );


    offers.forEach(
      (offer) => {

        const name =
          getOfferName(
            offer
          );


        /*
         * Buy 1 / Carton do not use
         * internal mixed allocation.
         */
        if (
          isBuy1(name) ||
          isCarton(name)
        ) {

          offer
            .querySelectorAll(
              '.' +
              CONFIG.classes
                .hideStockSwatch
            )
            .forEach(
              (swatch) => {

                swatch.classList.remove(
                  CONFIG.classes
                    .hideStockSwatch
                );

              }
            );


          return;
        }


        const usage =
          new Map();


        const groups =
          Array.from(
            offer.querySelectorAll(
              '.js-bx-variant-group'
            )
          );


        groups.forEach(
          (variantGroup) => {

            /*
             * =========================================
             * A. SHOW / HIDE INDIVIDUAL SWATCHES
             * =========================================
             */

            const swatches =
              Array.from(
                variantGroup.querySelectorAll(
                  '.bx-swatch[data-col]'
                )
              );


            swatches.forEach(
              (swatch) => {

                const swatchGroup =
                  swatch.closest(
                    '.bx-swatch-group'
                  );


                /*
                 * Ignore single hidden Size group.
                 */
                if (
                  swatchGroup &&
                  swatchGroup.classList.contains(
                    CONFIG.classes
                      .hideSingleSize
                  )
                ) {

                  swatch.classList.remove(
                    CONFIG.classes
                      .hideStockSwatch
                  );

                  return;
                }


                const variant =
                  getVariantForSwatch(
                    variantGroup,
                    swatch
                  );


                if (!variant) {

                  swatch.classList.remove(
                    CONFIG.classes
                      .hideStockSwatch
                  );

                  return;
                }


                const inventory =
                  getVariantStock(
                    variant
                  );


                const used =
                  usage.get(
                    String(
                      variant.id
                    )
                  ) || 0;


                const available =
                  (
                    inventory === Infinity ||
                    used < inventory
                  );


                swatch.classList.toggle(
                  CONFIG.classes
                    .hideStockSwatch,

                  !available
                );

              }
            );


            /*
             * =========================================
             * B. CHECK CURRENT ROW SELECTION
             * =========================================
             */

            let selectedOptions =
              getSelectedBundlexOptions(
                variantGroup
              );


            let selectedVariant =
              findShopifyVariantByOptions(
                selectedOptions
              );


            let selectedAvailable =
              false;


            if (selectedVariant) {

              const inventory =
                getVariantStock(
                  selectedVariant
                );


              const used =
                usage.get(
                  String(
                    selectedVariant.id
                  )
                ) || 0;


              selectedAvailable =
                (
                  inventory === Infinity ||
                  used < inventory
                );

            }


            /*
             * =========================================
             * C. AUTO SELECT FIRST VALID REPLACEMENT
             * =========================================
             */

            if (!selectedAvailable) {

              const optionGroups =
                Array.from(
                  variantGroup.querySelectorAll(
                    '.bx-swatch-group'
                  )
                );


              let replacementFound =
                false;


              for (
                const optionGroup
                of optionGroups
              ) {

                if (
                  optionGroup.classList.contains(
                    CONFIG.classes
                      .hideSingleSize
                  )
                ) {
                  continue;
                }


                const availableSwatches =
                  Array.from(
                    optionGroup.querySelectorAll(
                      '.bx-swatch[data-col]'
                    )
                  )
                    .filter(
                      (swatch) =>
                        !swatch.classList.contains(
                          CONFIG.classes
                            .hideStockSwatch
                        ) &&
                        !swatch.classList.contains(
                          'bx-swatch--unavailable'
                        ) &&
                        !swatch.classList.contains(
                          'bx-swatch--hidden'
                        )
                    );


                if (
                  !availableSwatches.length
                ) {
                  continue;
                }


                const replacement =
                  availableSwatches.find(
                    (swatch) => {

                      const variant =
                        getVariantForSwatch(
                          variantGroup,
                          swatch
                        );


                      if (!variant) {
                        return false;
                      }


                      const inventory =
                        getVariantStock(
                          variant
                        );


                      const used =
                        usage.get(
                          String(
                            variant.id
                          )
                        ) || 0;


                      return (
                        variant.available !== false &&
                        (
                          inventory === Infinity ||
                          used < inventory
                        )
                      );
                    }
                  );


                if (replacement) {

                  if (
                    !replacement.classList.contains(
                      'bx-swatch--active'
                    ) &&
                    !autoSelecting
                  ) {

                    autoSelecting =
                      true;


                    replacement.click();


                    setTimeout(
                      () => {

                        autoSelecting =
                          false;

                        scheduleUpdate();

                      },
                      30
                    );

                  }


                  selectedVariant =
                    getVariantForSwatch(
                      variantGroup,
                      replacement
                    );


                  replacementFound =
                    true;


                  break;
                }

              }


              if (
                !replacementFound
              ) {
                selectedVariant =
                  null;
              }

            }


            /*
             * =========================================
             * D. ALLOCATE CURRENT ROW INVENTORY
             * =========================================
             */

            if (selectedVariant) {

              const id =
                String(
                  selectedVariant.id
                );


              usage.set(
                id,
                (usage.get(id) || 0) + 1
              );

            }

          }
        );

      }
    );
  }


  /*
   * =========================================================
   * MAIN UPDATE
   * =========================================================
   */

  function updateBundlex() {

    /*
     * FIRST:
     *
     * If current Shopify variant
     * is sold out, hide entire
     * Bundlex widget immediately.
     */
    const outOfStock =
      updateBundlexVisibilityForSelectedVariant();


    if (outOfStock) {
      return;
    }


    updateSingleSizeOptions();

    updateVariantSelectorVisibility();

    updateStockVisibility();

    updateBundlexVariantInventory();
  }


  /*
   * =========================================================
   * UPDATE SCHEDULER
   * =========================================================
   */

  function scheduleUpdate() {

    if (updateScheduled) {
      return;
    }


    updateScheduled =
      true;


    requestAnimationFrame(
      () => {

        updateScheduled =
          false;

        updateBundlex();

      }
    );
  }


  /*
   * =========================================================
   * FIND BUNDLEX ROOT
   * =========================================================
   */

  function findBundlexRoot() {

    /*
     * Best match based on your
     * real Bundlex HTML.
     */
    const widgetContainer =
      document.querySelector(
        '.bx-widget-container'
      );


    if (widgetContainer) {
      return widgetContainer;
    }


    const offers =
      document.querySelector(
        '.bx-offers'
      );


    if (offers) {

      return (
        offers.closest(
          '.bx-widget-container,' +
          '.bx-widget,' +
          '.bx-wrapper,' +
          '.bx-container,' +
          '.bx-bundle,' +
          '.bx-app,' +
          '[class*="bundlex"]'
        ) ||
        offers.parentElement ||
        offers
      );

    }


    const firstOffer =
      document.querySelector(
        '.js-bx-offer'
      );


    if (!firstOffer) {
      return null;
    }


    return (
      firstOffer.parentElement
    );
  }


  /*
   * =========================================================
   * OBSERVER
   * =========================================================
   */

  function startObserver() {

    bundlexRoot =
      findBundlexRoot();


    if (!bundlexRoot) {

      initAttempts++;


      if (
        initAttempts <=
        CONFIG.maxInitAttempts
      ) {

        setTimeout(
          startObserver,
          CONFIG.retryDelay
        );

      }


      return;
    }


    /*
     * Initial run.
     */
    scheduleUpdate();


    /*
     * Observe Bundlex only.
     */
    observer =
      new MutationObserver(
        (mutations) => {

          const relevant =
            mutations.some(
              (mutation) => {

                if (
                  mutation.type ===
                  'childList'
                ) {

                  return (
                    mutation
                      .addedNodes
                      .length > 0 ||
                    mutation
                      .removedNodes
                      .length > 0
                  );

                }


                if (
                  mutation.type ===
                  'attributes'
                ) {
                  return true;
                }


                return false;
              }
            );


          if (relevant) {
            scheduleUpdate();
          }

        }
      );


    observer.observe(
      bundlexRoot,
      {
        childList: true,
        subtree: true,

        attributes: true,

        attributeFilter: [
          'class',
          'aria-selected',
          'aria-checked',
          'aria-expanded'
        ]
      }
    );


    /*
     * Bundlex interactions.
     */
    bundlexRoot.addEventListener(
      'click',
      () => {

        setTimeout(
          scheduleUpdate,
          20
        );


        setTimeout(
          scheduleUpdate,
          100
        );


        setTimeout(
          scheduleUpdate,
          300
        );

      },
      true
    );


    bundlexRoot.addEventListener(
      'change',
      scheduleUpdate,
      true
    );


    /*
     * Shopify variant CHANGE events.
     */
    document.addEventListener(
      'change',
      (event) => {

        if (
          event.target &&
          (
            event.target.matches(
              '[name="id"]'
            ) ||
            event.target.closest(
              '.hdt-product-form__values,' +
              '.hdt-swatch,' +
              '.hdt-product-form__label,' +
              '.nt_select,' +
              '.swatch,' +
              '[data-swatch-item],' +
              '[data-value]'
            )
          )
        ) {

          setTimeout(
            scheduleUpdate,
            20
          );


          setTimeout(
            scheduleUpdate,
            100
          );


          setTimeout(
            scheduleUpdate,
            250
          );


          setTimeout(
            scheduleUpdate,
            500
          );

        }

      },
      true
    );


    /*
     * Shopify variant CLICK events.
     *
     * Needed because some theme variant
     * pickers update URL / hidden input
     * after click rather than immediately.
     */
    document.addEventListener(
      'click',
      (event) => {

        if (
          event.target.closest(
            '.hdt-product-form__values,' +
            '.hdt-swatch,' +
            '.hdt-product-form__label,' +
            '.nt_select,' +
            '.swatch,' +
            '[data-swatch-item],' +
            '[data-value]'
          )
        ) {

          setTimeout(
            scheduleUpdate,
            20
          );


          setTimeout(
            scheduleUpdate,
            100
          );


          setTimeout(
            scheduleUpdate,
            250
          );


          setTimeout(
            scheduleUpdate,
            500
          );

        }

      },
      true
    );


    /*
     * Extra URL watcher.
     *
     * Some Shopify themes use
     * history.replaceState / pushState
     * when changing ?variant=.
     */
    let lastUrl =
      window.location.href;


    setInterval(
      () => {

        if (
          window.location.href !==
          lastUrl
        ) {

          lastUrl =
            window.location.href;

          scheduleUpdate();

        }

      },
      250
    );

  }


  /*
   * =========================================================
   * INIT
   * =========================================================
   */

  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      startObserver,
      {
        once: true
      }
    );

  } else {

    startObserver();

  }

})();